import type { MRT_Row, MRT_RowData, MRT_RowSelectionState } from 'material-react-table'
import { createContext, useCallback, useContext, useSyncExternalStore } from 'react'

type Listener = () => void

export type MrtRowSelectionStore = {
  subscribe: (listener: Listener) => () => void
  getSelection: () => MRT_RowSelectionState
  setSelection: (selection: MRT_RowSelectionState) => void
}

export const createMrtRowSelectionStore = (): MrtRowSelectionStore => {
  const listeners = new Set<Listener>()
  let current: MRT_RowSelectionState = {}
  return {
    subscribe: listener => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    getSelection: () => current,
    setSelection: selection => {
      if (selection === current) return
      current = selection
      listeners.forEach(listener => listener())
    },
  }
}

export const MrtRowSelectionStoreContext = createContext<MrtRowSelectionStore>(createMrtRowSelectionStore())

export const getRowSelectionSnapshot = <T extends MRT_RowData>(
  row: MRT_Row<T>,
  selection: MRT_RowSelectionState,
): string => `${Boolean(selection[row.id])}:${row.subRows?.filter(subRow => selection[subRow.id]).length ?? 0}`

export const useMrtRowSelectionSnapshot = <T extends MRT_RowData>(row: MRT_Row<T>): string => {
  const { subscribe, getSelection } = useContext(MrtRowSelectionStoreContext)
  const getSnapshot = useCallback(() => getRowSelectionSnapshot(row, getSelection()), [row, getSelection])
  return useSyncExternalStore(subscribe, getSnapshot)
}
